// Central application state hook — tenant-scoped
import { useCallback, useMemo } from 'react';
import { forecastControlAt } from './forecastControl.js';
import { assembleControlAssuranceCase } from './reasoning/assuranceCase.js';
import { buildEvaluationTasks } from '../utils/obligationEvaluations.js';
import { evaluateObligations, evaluateAllObligations } from '../engine/obligationEvaluator.js';
import { buildControlIndex } from '../constants/controlIndex.js';
import { buildControlDomainContext } from './controlDomainContext.js';
import { usePersistence } from './usePersistence.js';
import { LEGACY_TO_SCF } from '../constants/scfMigration.js';
import { logActivity } from '../api/activityLogger.js';
import { DOC_STATUS, normalizeDocStatus } from '../utils/documentWorkflow.js';
import {
  buildOverrideAndProjection,
  buildOwnedSoftwareEntry,
} from './vulnerabilities/projectOverride.js';
import { useDocumentState } from './useDocumentState.js';
import {
  collectTaskSources,
  isOpenTaskStatus,
  riskAiAssessmentTasks,
} from '../selectors/assignments.js';
import { useOpenWorkflowSteps, useOpenHaltReviews } from '../hooks/useMyWorkflowSteps.js';
import { useRiskAiAssessmentList } from '../hooks/useRiskAiAssessmentList.js';
import { useRiskState } from './useRiskState.js';
import { useGovernanceActions } from './actions/useGovernanceActions.js';
import { usePeopleActions } from './actions/usePeopleActions.js';
import { useRegistryActions } from './actions/useRegistryActions.js';
import { useArtefactActions } from './actions/useArtefactActions.js';
import { useBcdrActions } from './actions/useBcdrActions.js';
import { useIncidentActions } from './actions/useIncidentActions.js';

const EMPTY_USERS = [];

// `users` (the tenant's users) decides who owes an undecided AI assessment
// whose author is no longer a user (riskAiAssessmentTasks, RAI-05).
export function useAppState(tenantId, session, users = EMPTY_USERS) {
  const {
    state,
    stateReady,
    stateLoadError,
    updateState,
    reloadState,
    flushPendingSave,
    stateRef,
    adoptStateVersion,
    beginExternalStateWrite,
    adoptServerEmployee,
    adoptServerTasks,
  } = usePersistence(tenantId, session);

  // ── Document actions ─────────────────────────────────
  const documentActions = useDocumentState({
    updateState,
    tenantId,
    stateRef,
    adoptStateVersion,
    beginExternalStateWrite,
  });

  // ── Vulnerability triage actions ─────────────────────────
  // Boundary VUL-B-003: triage decisions live in vulnerabilityOverrides[].
  // The dashboard projects them onto state.vulnerabilities[] optimistically;
  // the next correlator tick (hourly :15) recomputes from canonical inputs.
  const saveVulnerabilityOverride = useCallback(
    (vuln, decision, opts) => {
      const session = opts?.session;
      const nowIso = new Date().toISOString();
      const createdBy = session?.displayName || session?.username || 'unknown';
      const { override, projectedVuln } = buildOverrideAndProjection(vuln, decision, {
        justification: opts?.justification || '',
        payload: opts?.payload,
        createdBy,
        nowIso,
      });
      updateState((s) => {
        s.vulnerabilityOverrides = [...(s.vulnerabilityOverrides || []), override];
        const idx = (s.vulnerabilities || []).findIndex(
          (v) => v.vulnerabilityKey === projectedVuln.vulnerabilityKey
        );
        if (idx >= 0) s.vulnerabilities[idx] = projectedVuln;
      });
      if (session) {
        logActivity(tenantId, session, {
          module: 'Vulnerabilities',
          actionType: `vulnerability.${
            decision === 'set-actor'
              ? 'actor_overridden'
              : decision === 'assign'
                ? 'assigned'
                : decision === 'set-due-date'
                  ? 'due_date_set'
                  : decision
          }`,
          recordId: override.id,
          recordLabel: `${vuln.cveId} on ${vuln.assetId || vuln.softwareId || 'unmatched'}`,
        });
      }
      return override;
    },
    [updateState, tenantId]
  );

  const archiveVulnerabilityOverride = useCallback(
    (overrideId, session) => {
      let archivedOverride = null;
      updateState((s) => {
        const idx = (s.vulnerabilityOverrides || []).findIndex((o) => o.id === overrideId);
        if (idx < 0) return;
        archivedOverride = {
          ...s.vulnerabilityOverrides[idx],
          archivedAt: new Date().toISOString(),
        };
        s.vulnerabilityOverrides[idx] = archivedOverride;
      });
      if (session && archivedOverride) {
        logActivity(tenantId, session, {
          module: 'Vulnerabilities',
          actionType: 'vulnerability.override_archived',
          recordId: overrideId,
          recordLabel: archivedOverride.cveId,
        });
      }
    },
    [updateState, tenantId]
  );

  const setSoftwareOwnership = useCallback(
    (vendor, product, classification, session) => {
      const nowIso = new Date().toISOString();
      const createdBy = session?.displayName || session?.username || 'unknown';
      const entry = buildOwnedSoftwareEntry(vendor, product, classification, { createdBy, nowIso });
      updateState((s) => {
        const reg = (s.ownedSoftwareRegistry || []).filter(
          (e) =>
            !(
              String(e.vendor).toLowerCase() === String(vendor).toLowerCase() &&
              String(e.product).toLowerCase() === String(product).toLowerCase()
            )
        );
        s.ownedSoftwareRegistry = [...reg, entry];
      });
      if (session) {
        logActivity(tenantId, session, {
          module: 'Vulnerabilities',
          actionType: `owned_software.${classification === 'internal' ? 'added' : 'reclassified'}`,
          recordId: entry.id,
          recordLabel: `${vendor} ${product} → ${classification}`,
        });
      }
      return entry;
    },
    [updateState, tenantId]
  );

  const linkVulnerabilityToRisk = useCallback(
    (vuln, riskId, session) => {
      updateState((s) => {
        const idx = (s.vulnerabilities || []).findIndex(
          (v) => v.vulnerabilityKey === vuln.vulnerabilityKey
        );
        if (idx >= 0) {
          const existing = s.vulnerabilities[idx];
          if (!existing.linkedRiskIds?.includes(riskId)) {
            s.vulnerabilities[idx] = {
              ...existing,
              linkedRiskIds: [...(existing.linkedRiskIds || []), riskId],
            };
          }
        }
        const ridx = (s.risks || []).findIndex((r) => r.id === riskId);
        if (ridx >= 0) {
          const r = s.risks[ridx];
          const newLinked = r.linkedVulnerabilityIds?.includes(vuln.id)
            ? r.linkedVulnerabilityIds
            : [...(r.linkedVulnerabilityIds || []), vuln.id];
          s.risks[ridx] = { ...r, linkedVulnerabilityIds: newLinked };
        }
      });
      if (session) {
        logActivity(tenantId, session, {
          module: 'Vulnerabilities',
          actionType: 'vulnerability.promoted_to_risk',
          recordId: vuln.id,
          recordLabel: `${vuln.cveId} → ${riskId}`,
        });
      }
    },
    [updateState, tenantId]
  );

  // ── Risk actions ─────────────────────────────────────────
  const riskActions = useRiskState({ updateState, tenantId, stateRef });

  // ── Domain action sub-hooks ─────────────────────────────
  // Simple-CRUD action callbacks live in sibling hooks under
  // ./actions/. Each sub-hook captures the same closures (updateState,
  // tenantId, session) and returns the same callback identities; the
  // returned objects are spread into the public API below.
  const governanceActions = useGovernanceActions({ updateState, tenantId, session });
  const peopleActions = usePeopleActions({
    updateState,
    tenantId,
    session,
    state,
    flushPendingSave,
    adoptStateVersion,
    beginExternalStateWrite,
    adoptServerEmployee,
    adoptServerTasks,
  });
  const registryActions = useRegistryActions({ updateState, tenantId, stateRef });
  const artefactActions = useArtefactActions({ updateState, tenantId });
  const bcdrActions = useBcdrActions({ updateState, tenantId, flushPendingSave });
  const incidentActions = useIncidentActions({ updateState, tenantId });

  // ── Computed ────────────────────────────────────────────
  const controlIndex = useMemo(
    () => buildControlIndex((state.frameworks || []).map((f) => f.id)),
    [state.frameworks]
  );
  const allControls = controlIndex.allFlat;

  const coveredIds = useMemo(
    () =>
      new Set(
        (state.documents || []).flatMap((d) =>
          (d.controls || []).map((c) => (typeof c === 'string' ? c : c.controlId))
        )
      ),
    [state.documents]
  );

  // "Compliant" = published — the legacy `status` field (compliant/partial/
  // non-compliant/pending) is dead; documentStatus is the maintained lifecycle.
  const compliantDocs = (state.documents || []).filter(
    (d) => normalizeDocStatus(d.documentStatus) === DOC_STATUS.PUBLISHED
  );

  const evaluatorTasks = useMemo(() => {
    return evaluateObligations(state.enabledObligations || [], state);
  }, [
    state.enabledObligations,
    state.documents,
    state.risks,
    state.vendors,
    state.employees,
    state.assets,
    // PDWB-03: the `workflow-run-current` criterion and the workflow annotation.
    state.workflowEvidence,
  ]);

  // All obligation statuses including fulfilled — needed by Gate 5 of the control engine
  // so it can see that obligations ARE linked even when all criteria are met.
  const allObligationStatuses = useMemo(() => {
    return evaluateAllObligations(state.enabledObligations || [], state);
  }, [
    state.enabledObligations,
    state.documents,
    state.risks,
    state.vendors,
    state.employees,
    state.assets,
    // PDWB-03: the `workflow-run-current` criterion and the workflow annotation.
    state.workflowEvidence,
  ]);

  // msr 4.5b (CTX-04): an obligation whose evaluation by a person falls due
  // is a Tier 1 obligation-derived task beside the evaluator's own — computed
  // here, never stored (src/utils/obligationEvaluations.js). The evaluator is
  // read, never changed; these tasks never reach Gate 5, which reads
  // allObligationStatuses.
  const obligationTasks = useMemo(
    () => [...evaluatorTasks, ...buildEvaluationTasks(state, allObligationStatuses)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      evaluatorTasks,
      allObligationStatuses,
      state.obligationEvaluations,
      state.obligationCycleAnchors,
      state.obligationProjections,
      state.findings,
      state.artefacts,
    ]
  );

  // Unified task view — obligation tasks, ad-hoc tasks and every Tier 2
  // domain action (risk, evidence-request, management-review), from the one
  // task list the calendar and Action Centre read (OPP004A-03).
  // Open workflow steps (PDWB-02, design §3b.2) — a Tier 2 source of the one
  // list, read from the server, never stored here. For an admin or manager it
  // is every person's open step, so the tenant-wide overdue / due-soon figures
  // and the Tasks page and calendar they drill into count the same work
  // (GH #7583). Anyone else may read only their own steps (WF-B-003):
  // `allTasksScope` says so, and a tenant-wide figure must not present that
  // list as the tenant's total.
  const openSteps = useOpenWorkflowSteps(session, tenantId);
  const workflowSteps = openSteps.steps;
  // Reviews of halted recurring workflows (PDW-HALT-2) — the same scope rule
  // as the steps, and folded into the same completeness: a list missing them
  // is no more the tenant's total than one missing the steps.
  const openHaltReviews = useOpenHaltReviews(session, tenantId);
  const haltReviews = openHaltReviews.reviews;
  const allTasksScope = useMemo(
    () => ({
      // Before the first read lands the list is empty, not complete.
      workflowSteps:
        openSteps.failed || openHaltReviews.failed
          ? 'unavailable'
          : !openSteps.loaded || !openHaltReviews.loaded
            ? 'not-read'
            : openSteps.scope,
      partial: openSteps.partial || openHaltReviews.partial,
    }),
    [
      openSteps.failed,
      openSteps.loaded,
      openSteps.scope,
      openSteps.partial,
      openHaltReviews.failed,
      openHaltReviews.loaded,
      openHaltReviews.partial,
    ]
  );
  // Undecided AI risk assessments (RAI-05) — the "decide on AI assessment"
  // Tier 2 source, shaped by the shared riskAiAssessmentTasks.
  const riskAiAssessments = useRiskAiAssessmentList(session, tenantId);
  const riskAiTasks = useMemo(
    () => riskAiAssessmentTasks(riskAiAssessments, users),
    [riskAiAssessments, users]
  );

  // The tenant's open work — questions are never in it: a question is private
  // to the person it is assigned to (COMM-B-011), so it is counted only in
  // that person's own figures (useMyActionItems), never tenant-wide (GH #7583).
  const allTasks = useMemo(
    () => collectTaskSources(state, obligationTasks, workflowSteps, [], riskAiTasks, haltReviews),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      obligationTasks,
      workflowSteps,
      haltReviews,
      riskAiTasks,
      state.adhocTasks,
      state.risks,
      state.evidenceRequests,
      state.managementReviews,
      state.artefacts,
    ]
  );

  const [overdueTasks, openTasks] = useMemo(() => {
    const now = new Date();
    return [
      // A cancelled action is not owed work, so it is never overdue (D-2 A).
      allTasks.filter((t) => isOpenTaskStatus(t.status) && t.dueDate && new Date(t.dueDate) < now),
      // A status whitelist for obligation / ad-hoc tasks. It reaches the routed
      // pages via commonProps, where ArtefactRegister's email import reads it
      // as "open tasks on the control". Tier 2 `pending` is left out on
      // purpose. The one open-work rule for Tier 2 is isOpenTaskStatus
      // (OPP004A-03).
      allTasks.filter(
        (t) =>
          t.status === 'open' ||
          t.status === 'in-progress' ||
          t.status === 'overdue' ||
          t.status === 'unmet' ||
          t.status === 'partially-satisfied'
      ),
    ];
  }, [allTasks]);

  const { evalControl, forecastAt, assembleAssuranceCase } = useMemo(() => {
    const cache = new Map();
    // One builder, shared with the measurement control-status base measure
    // (GH #7115), so the server evaluates controls over the same inputs.
    const domainContext = buildControlDomainContext(state, allObligationStatuses);
    // Shared evaluation core, parameterised by the `asOf` instant. The live
    // path passes asOf=undefined (=> now); the Audit-Readiness Forecast passes a
    // future date T. Both read the identical domainContext, so the forecast's
    // "today" column is provably the same as the rest of the app (AC-003).
    const run = (controlId, asOf) => {
      let result = forecastControlAt(controlId, state.documents, domainContext, asOf);
      // If nothing found with the native ID, retry with its TCF equivalent.
      // This handles the transition period where state.frameworks still stores
      // native IDs (A.5.1, CIS-1.1 …) but documents have already been migrated
      // to TCF IDs by migrateState().
      if (result.docs.length === 0) {
        const scfId = LEGACY_TO_SCF[controlId];
        if (scfId) result = forecastControlAt(scfId, state.documents, domainContext, asOf);
      }
      return result;
    };
    const evalControlFn = (controlId) => {
      if (cache.has(controlId)) return cache.get(controlId);
      const result = run(controlId); // asOf omitted → live now
      cache.set(controlId, result);
      return result;
    };
    // Read-only projection — never cached (asOf varies per call) and never
    // writes state. Signature: (controlId, asOf) => { status, gates, docs }.
    const forecastAtFn = (controlId, asOf) => run(controlId, asOf);

    // B1 assurance-case assembler, bound to the same domainContext + documents
    // as the live gates so the composed argument can never disagree with the
    // status shown elsewhere. Read-only, never cached (asOf/auditDate vary).
    // Mirrors run()'s native→TCF fallback: if the given id surfaces no documents
    // (state.frameworks may still hold native ids while docs are already on TCF
    // ids), assemble against the TCF equivalent instead.
    const assembleAssuranceCaseFn = (controlId, opts = {}) => {
      const { asOf, auditDate } = opts;
      let evalId = controlId;
      const probe = forecastControlAt(controlId, state.documents, domainContext, asOf);
      if (probe.docs.length === 0 && LEGACY_TO_SCF[controlId]) {
        evalId = LEGACY_TO_SCF[controlId];
      }
      return assembleControlAssuranceCase(evalId, state.documents, domainContext, {
        asOf,
        auditDate,
      });
    };

    return {
      evalControl: evalControlFn,
      forecastAt: forecastAtFn,
      assembleAssuranceCase: assembleAssuranceCaseFn,
    };
  }, [
    state.documents,
    state.artefacts,
    state.risks,
    state.riskObligations,
    state.employees,
    state.documentTypes,
    allObligationStatuses,
    state.controlOwnership,
    state.ownershipSettings,
    state.obligationDecisions,
    state.artefactSettings,
    state.controlEffectiveness,
    state.integrations,
    state.engineRunLog,
    // PDWB-03: gate 4's workflow evidence (and the #6608 marker that withholds it).
    state.workflowEvidence,
    state.__planGated,
  ]);

  const ctrlStatus = useCallback((controlId) => evalControl(controlId).status, [evalControl]);

  return {
    state,
    updateState,
    stateReady,
    stateLoadError,
    reloadState,
    flushPendingSave,
    adoptStateVersion,
    beginExternalStateWrite,
    ...documentActions,
    saveVulnerabilityOverride,
    archiveVulnerabilityOverride,
    setSoftwareOwnership,
    linkVulnerabilityToRisk,
    ...riskActions,
    // Domain action sub-hooks (governance, people, registry, BCDR, incidents).
    //
    // These spreads must not export overlapping keys. Precedence here is
    // last-one-wins and completely silent, so a collision does not fail, warn,
    // or show up in a diff of either sub-hook — it just decides which
    // implementation the whole app gets, from the ORDER OF THESE LINES.
    //
    // That was not hypothetical: useGovernanceActions exported a saveArtefact
    // that only wrote to the state blob and issued no request at all, while
    // useArtefactActions exports the ARTA-03 one that calls artefacts-write.
    // The correct one won solely because `...artefactActions` sits below
    // `...governanceActions`. Moving either line would have silently reverted
    // artefact creation to a version that never reaches the server — and since
    // ARTA-04 strips `artefacts` from the blob, the record would have appeared
    // optimistically and vanished on the next reload, with no error anywhere.
    // The dead pair has been removed; keep it that way.
    ...governanceActions,
    ...peopleActions,
    ...registryActions,
    ...artefactActions,
    ...bcdrActions,
    ...incidentActions,
    allControls,
    controlIndex,
    coveredIds,
    overdueTasks,
    openTasks,
    compliantDocs,
    obligationTasks,
    workflowSteps,
    haltReviews,
    riskAiTasks,
    allTasks,
    allTasksScope,
    evaluateControl: evalControl,
    getControlStatus: ctrlStatus,
    // Read-only Audit-Readiness Forecast projection: (controlId, asOf) =>
    // { status, gates, docs } evaluated as of instant `asOf` (ms/Date/ISO).
    // Shares evalControl's domainContext so asOf=now matches the live status.
    forecastControlAt: forecastAt,
    // Read-only B1 assurance-case assembler bound to the same context:
    // (controlId, { asOf, auditDate }) => ControlAssuranceCase. Used by the
    // reasoning-chain claim tree and the assurance-case export.
    assembleAssuranceCase,
  };
}
